import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  IllusSeasonal,
  Image,
  Modal,
  noop,
  theme,
  TitleText,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useDeliveryMenuNavigation } from '../../screens/DeliveryMenuScreen/DeliveryMenu.navigation';

export const DeliveryInFlightModal = (props: DeliveryInFlightModalProps) => {
  const { canGoBackFromDeliveryInFlight, isValidDeliveryInFlight } =
    useDeliveryMenuNavigation();

  return (
    <Modal
      animationType="fade"
      size="medium"
      visible={isValidDeliveryInFlight ? Boolean(props.orderId) : false}
      onRequestClose={canGoBackFromDeliveryInFlight ? props.onClose : noop}
    >
      <DeliveryInFlightContent
        orderId={props.orderId}
        canGoBack={canGoBackFromDeliveryInFlight}
        onClose={props.onClose}
        onGoHome={props.onGoHome}
        onViewOrder={props.onViewOrder}
      />
    </Modal>
  );
};

export const DeliveryInFlightContent = ({
  orderId,
  canGoBack,
  onClose,
  ...navigation
}: DeliveryInFlightContentProps) => {
  const { t } = useLocalizationContext();
  const onGoHome = React.useCallback(() => {
    onClose();
    navigation.onGoHome();
  }, [navigation, onClose]);
  const onGoOrderStatus = React.useCallback(() => {
    onClose();
    navigation.onViewOrder(orderId);
  }, [orderId, navigation, onClose]);

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <DesktopImage />
        <View style={styles.info}>
          <TitleText style={styles.title} size={5}>
            {t('delivery.in-flight-modal.title')}
          </TitleText>
          <BodyText>{t('delivery.in-flight-modal.subtitle')}</BodyText>
        </View>
      </View>
      <View style={[styles.row, styles.buttons]}>
        <Button
          size="large"
          palette="secondary"
          width="47.5%"
          onPress={canGoBack ? onClose : onGoHome}
        >
          {canGoBack
            ? t('delivery.in-flight-modal.cta-close')
            : t('delivery.in-flight-modal.cta-home')}
        </Button>
        <Button size="large" width="47.5%" onPress={onGoOrderStatus}>
          {t('delivery.in-flight-modal.cta-status')}
        </Button>
      </View>
    </View>
  );
};

const DesktopImage = () => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return null;

  return (
    <Image
      source={IllusSeasonal}
      style={styles.image}
      accessibilityIgnoresInvertColors
    />
  );
};

type DeliveryInFlightModalProps = {
  readonly orderId: string;
  readonly onClose: () => void;
  readonly onGoHome: () => void;
  readonly onViewOrder: (orderId: string) => void;
};

type DeliveryInFlightContentProps = {
  readonly canGoBack: boolean;
  readonly orderId: string;
  readonly onClose: () => void;
  readonly onGoHome: () => void;
  readonly onViewOrder: (orderId: string) => void;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing.large,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  buttons: {
    marginTop: theme.spacing.small + theme.spacing.xsmall,
  },
  info: {
    flex: 1,
  },
  title: {
    marginBottom: theme.spacing.small,
  },
  image: {
    width: 200,
    height: 200,
    marginRight: theme.spacing.small + theme.spacing.xsmall,
  },
});
